import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

const swedishPostalCode = /^\d{3}\s?\d{2}$/;
const phone = /^[\d\s\-+()]{5,}$/;

const spouseSchema = z.object({
  name: z.string().min(1),
  email: z.string().email().nullable(),
  phone: z.string().regex(phone).nullable(),
});

export const memberContactSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  address: z.string().min(1),
  postalCode: z.string().regex(swedishPostalCode, 'Ogiltigt postnummer'),
  city: z.string().min(1),
  alternateAddress: z.string().nullable(),
  phoneLandline: z.string().regex(phone).nullable(),
  phoneMobile: z.string().regex(phone).nullable(),
  spouse: spouseSchema.nullable(),
});

export type MemberContact = z.infer<typeof memberContactSchema>;

export interface Member extends MemberContact {
  email: string;
  role: 'member' | 'board';
  status: 'active' | 'former';
  boatPlaceIds: string[];
  joinedAt: Timestamp;
  invitedAt: Timestamp | null;
  firstLoginAt: Timestamp | null;
  updatedAt: Timestamp;
}

export interface UseMemberResult {
  member: Member | null;
  loading: boolean;
  error: Error | null;
}

export function useMember(uid: string | null): UseMemberResult {
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!uid) {
      setMember(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const ref = doc(db, 'members', uid);
    return onSnapshot(
      ref,
      (snap) => {
        setMember(snap.exists() ? (snap.data() as Member) : null);
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e);
        setLoading(false);
      },
    );
  }, [uid]);

  return { member, loading, error };
}

export async function saveMemberContact(
  uid: string,
  contact: MemberContact,
): Promise<void> {
  await updateDoc(doc(db, 'members', uid), {
    ...contact,
    updatedAt: serverTimestamp(),
  });
}

export interface AdminMemberContact extends MemberContact {
  email: string;
  status: 'active' | 'former';
}

export async function saveMemberAdmin(
  uid: string,
  data: AdminMemberContact,
): Promise<void> {
  await updateDoc(doc(db, 'members', uid), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export interface UseAllMembersResult {
  members: (Member & { id: string })[];
  loading: boolean;
  error: Error | null;
}

export function useAllMembers(): UseAllMembersResult {
  const [members, setMembers] = useState<(Member & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'members'));
    return onSnapshot(
      q,
      (snap) => {
        setMembers(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Member) })),
        );
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e);
        setLoading(false);
      },
    );
  }, []);

  return { members, loading, error };
}
